import { Link } from '@tanstack/react-router';
import { ChevronRight, ShieldAlert, Users2 } from 'lucide-react';

import { AdminLayout } from '@/app/shell/admin-layout';
import { type AdminPlan, useAdminPlans } from '@/shared/api/admin-plans';
import { cn } from '@/shared/lib/cn';

export function PlansListPage() {
  const { data: plans, isLoading } = useAdminPlans();

  return (
    <AdminLayout>
      <header className="mb-4">
        <h1 className="text-xl font-semibold">Plans</h1>
        <p className="text-sm text-neutral-400">
          Tier defaults — limits + features shared by every team on the plan.  Per-team exceptions
          live in <Link to="/teams" className="text-orange-400 hover:underline">Teams</Link> → limit overrides.
        </p>
      </header>

      <div className="rounded-lg border border-amber-900/40 bg-amber-950/30 p-4 text-sm text-amber-200">
        <div className="mb-1 flex items-center gap-2 font-medium">
          <ShieldAlert size={14} /> Plan changes affect every team on this plan.
        </div>
        <p className="text-amber-300/80">
          Use the preview button before applying.  Free-plan changes have a 24-hour cooldown after each
          edit.  Revertible within 30 days from the history panel.
        </p>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {isLoading && !plans ? (
          <div className="col-span-full text-sm text-neutral-500">Loading…</div>
        ) : (
          plans?.map((p) => <PlanCard key={p.code} plan={p} />)
        )}
      </div>
    </AdminLayout>
  );
}

function PlanCard({ plan }: { plan: AdminPlan }) {
  return (
    <Link
      to="/plans/$code"
      params={{ code: plan.code }}
      className="group block rounded-lg border border-neutral-800 bg-neutral-900 p-5 transition-colors hover:bg-neutral-800/40 hover:ring-1 hover:ring-orange-500/50"
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-neutral-100">{plan.name}</h2>
        <div className="text-xs text-neutral-500">
          <code>{plan.code}</code>
        </div>
      </div>

      <div className="mb-3 flex items-baseline gap-1">
        <span className="text-2xl font-semibold text-neutral-100">
          ${(plan.monthlyPriceCents / 100).toFixed(0)}
        </span>
        <span className="text-xs text-neutral-500">/ month</span>
      </div>

      <dl className="mb-3 space-y-1 text-sm">
        <Row label="Members" value={plan.maxMembers === null ? 'Unlimited' : String(plan.maxMembers)} />
        <Row label="Projects" value={plan.maxProjects === null ? 'Unlimited' : String(plan.maxProjects)} />
        <Row
          label="Storage"
          value={
            plan.storageQuotaPerSeatBytes !== null
              ? `${formatBytes(plan.storageQuotaPerSeatBytes)} / seat`
              : formatBytes(plan.storageQuotaBytes)
          }
        />
        <Row label="Max file" value={formatBytes(plan.maxFileSizeBytes)} />
      </dl>

      <div className="flex items-center justify-between border-t border-neutral-800 pt-3 text-xs">
        <div className="flex items-center gap-1 text-neutral-400">
          <Users2 size={12} /> {plan.teamCount} teams
        </div>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1',
            plan.isActive
              ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30'
              : 'bg-neutral-700/30 text-neutral-500 ring-neutral-700',
          )}
        >
          {plan.isActive ? 'active' : 'inactive'}
        </span>
      </div>

      <div className="mt-3 flex items-center justify-end gap-1 text-xs text-orange-400 group-hover:text-orange-300">
        Edit plan <ChevronRight size={12} />
      </div>
    </Link>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-neutral-200">{value}</dd>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(0)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
